import {
  Box,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import React, { useState } from "react";
import { FormattedMessage } from "react-intl";
import styled from "styled-components";
import { IGetEmployer } from "../../../../../../../../api/employer/apiGetEmployer";
import cuideoTheme from "../../../../../../../../containers/themes/defaultTheme";
import ViewInZohoButton from "../../../../../../components/ViewInZohoButton";
import FormSection from "../../../FormComponents/FormSection";
import EmployerFindModal from "./EmployerFindModal";

const StyledRadioGroup = styled(RadioGroup)`
  .MuiSvgIcon-root {
    width: 1.25rem;
    height: 1.25rem;
  }

  .MuiFormControlLabel-root {
    align-items: flex-start;
  }

  .MuiFormControlLabel-label {
    font-size: 0.875rem;
    color: ${cuideoTheme.palette.primary.light};
  }
`;

const StyledExistingAlert = styled(Alert)`
  .MuiAlert-message {
    font-size: 0.875rem;
    line-height: 1.35;
  }
`;

const StyledLabel = styled.div`
  padding: 0.5rem 0;
`;

const StyledLabelTitle = styled(Typography)`
  font-size: 0.875rem;
  color: ${cuideoTheme.palette.primary.light};
`;

const StyledLabelInfo = styled(Alert)`
  padding: 0.25rem 0.75rem;
  font-size: 0.8125rem;
  max-width: 20rem;
  background-color: rgb(250, 251, 252);

  .MuiSvgIcon-root {
    color: ${cuideoTheme.palette.primary.main};
  }
`;

type TOp = "new" | "existing" | "already_requested";

interface IEmployerSelectProps {
  op: TOp;
  employerCrmId?: string;
  employerCrmIdName?: string;
  onEmployerSelect: (employer: IGetEmployer) => void;
  onNewEmployer: () => void;
  onAlreadyRequestedEmployer: () => void;
}

const EmployerSelect = (props: IEmployerSelectProps) => {
  const {
    op,
    employerCrmId,
    employerCrmIdName,
    onEmployerSelect,
    onNewEmployer,
    onAlreadyRequestedEmployer,
  } = props;
  const [state, setState] = useState({
    open: false,
  });

  const handleRadioChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    value: string
  ) => {
    if (value === "new") {
      onNewEmployer();
    } else if (value === "already_requested") {
      onAlreadyRequestedEmployer();
    } else {
      setState({ open: true });
    }
  };

  const handleCancel = () => {
    setState({ open: false });
  };

  const handleEmployerSelect = (employer: IGetEmployer) => {
    setState((prevState) => ({ open: false }));
    onEmployerSelect(employer);
  };

  return (
    <FormSection>
      <FormControl component="fieldset">
        <StyledRadioGroup
          name="employerSelect"
          value={op}
          onChange={handleRadioChange}
        >
          <FormControlLabel
            value="new"
            control={<Radio color="primary" />}
            label={
              <StyledLabel>
                <StyledLabelTitle>
                  <FormattedMessage
                    id="EmployerSelect.Op.Crear nuevo"
                    defaultMessage="Crear nuevo"
                  />
                </StyledLabelTitle>
                <StyledLabelInfo severity="info">
                  <FormattedMessage
                    id="EmployerSelect.Con esta opción creas el empleador de cero. Es para clientes nuevos que nunca han tenido servicios con Cuideo."
                    defaultMessage="Con esta opción creas el empleador de cero. Es para clientes nuevos que nunca han tenido servicios con Cuideo."
                  />
                </StyledLabelInfo>
              </StyledLabel>
            }
          />
          <FormControlLabel
            value="existing"
            control={<Radio color="primary" />}
            label={
              <StyledLabel>
                <StyledLabelTitle>
                  <FormattedMessage
                    id="EmployerSelect.Op.Utilizar existente"
                    defaultMessage="Utilizar existente"
                  />
                </StyledLabelTitle>
                <StyledLabelInfo severity="info">
                  <FormattedMessage
                    id="EmployerSelect.Esta opción es para los clientes que ya han tenido o tienen servicios con nosotros y por lo tanto ya tenemos empleador. Recuerda que debes asociarlo."
                    defaultMessage="Esta opción es para los clientes que ya han tenido o tienen servicios con nosotros y por lo tanto ya tenemos empleador. Recuerda que debes asociarlo."
                  />
                </StyledLabelInfo>
              </StyledLabel>
            }
          />
          <FormControlLabel
            value="already_requested"
            control={<Radio color="primary" />}
            label={
              <StyledLabel>
                <StyledLabelTitle>
                  <FormattedMessage
                    id="EmployerSelect.Op.Ya solicitado"
                    defaultMessage="Ya solicitado"
                  />
                </StyledLabelTitle>
                <StyledLabelInfo severity="info">
                  <FormattedMessage
                    id="EmployerSelect.Esta opción es para cuando has valorado un servicio de un mismo cliente y ya le has solicitado el formulario recientemente con la opción de 'Crear Nuevo' (Ej: 24/7)"
                    defaultMessage="Esta opción es para cuando has valorado un servicio de un mismo cliente y ya le has solicitado el formulario recientemente con la opción de 'Crear Nuevo' (Ej: 24/7)"
                  />
                </StyledLabelInfo>
              </StyledLabel>
            }
          />
        </StyledRadioGroup>
        {employerCrmId && employerCrmIdName && (
          <StyledExistingAlert severity="warning">
            <FormattedMessage
              id="EmployerSelect.Se utilizará el empleador existente {name}"
              defaultMessage="Se utilizará el empleador existente {name}"
              values={{
                name: <strong>{employerCrmIdName}</strong>,
              }}
            />
            <Box mt={8}>
              <ViewInZohoButton zohoId={employerCrmId} zohoModule="Employer" />
            </Box>
          </StyledExistingAlert>
        )}
      </FormControl>
      <EmployerFindModal
        open={state.open}
        onCancel={handleCancel}
        onEmployerSelect={handleEmployerSelect}
      />
    </FormSection>
  );
};

export default EmployerSelect;
